import { captureException, captureMessage } from '@sentry/react';

const BASE_URL = '/api/';

type ParamValue = string | number | boolean | null | undefined;
type Params = Record<string, ParamValue | ParamValue[]>;

const buildSearchParams = (params: Params): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const toFormData = (data: Params): FormData => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  return formData;
};

interface ApiResponse<T = unknown> {
  data: T;
}

interface ApiError extends Error {
  response?: {
    data: unknown
  };
}

const request = async <T = unknown>(url: string, options: RequestInit = {}): Promise<ApiResponse<T>> => {
  const response = await fetch(`${BASE_URL}${url}`, options);

  let data: T;

  try {
    data = await response.json() as T;
  } catch (e) {
    captureException(e, { extra: { url, status: response.status, context: 'json_parse' } });
    const error: ApiError = new Error(`HTTP ${response.status}: invalid JSON response`);

    error.response = { data: undefined };
    throw error;
  }

  if (!response.ok) {
    const error: ApiError = new Error(`HTTP ${response.status}`);

    error.response = { data };

    if (response.status >= 500) {
      captureMessage(`API ${response.status}: ${url}`, {
        level: 'error',
        extra: { url, status: response.status }
      });
    }

    throw error;
  }

  return { data };
};

const get = <T = unknown>(url: string, params: Params = {}): Promise<ApiResponse<T>> => {
  const query = buildSearchParams(params);

  return request<T>(query ? `${url}?${query}` : url);
};

const post = <T = unknown>(url: string, data: Params): Promise<ApiResponse<T>> => request<T>(url, {
  method: 'POST',
  body: toFormData(data)
});

export const getBlogPosts = (params: Params) => get('blog/', params);

export const getObjects = (type: string, params: Params) => get(`${type}/`, params);

export const getSearch = (params: Params) => get('search/', params);

export const postOrder = (data: Params, objectId?: string) => post(
  objectId ? `order/${objectId}/` : 'order/',
  data
);

export const postShareFriend = (data: Params, lang: string, objectId: string) => post(
  `send-pdf/${objectId}/?lang=${lang}`,
  data
);

export const postSubscribe = (data: Params, lang: string) => post(
  `subscribe/?lang=${lang}`,
  data
);
